import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useDraft } from '@/hooks/useDraft'
import { usePeople } from '@/hooks/usePeople'
import { useTaskActions } from '@/hooks/useTaskActions'
import { useToday } from '@/hooks/useToday'
import { updateSettings } from '@/data/user'
import { goToTask } from '@/lib/reveal'
import type { TaskPatch } from '@/data/tasks'
import { addDays, dateOf, fmtDate, fmtDateLong } from '@/logic/dates'
import { dayHeading, repeatLabel } from '@/logic/labels'
import { moveOccurrence } from '@/logic/recurrence'
import { REMINDER_OFFSETS } from '@/logic/reminders'
import { isOpen, routineWeek } from '@/logic/status'
import { activeCategories, useCategories } from '@/store/categories'
import { useHousehold } from '@/store/household'
import { useTasks } from '@/store/tasks'
import type { DateStr, Recurrence, Task, TaskKind, TimeStr } from '@/types'
import { Button } from './Button'
import { Checklist } from './Checklist'
import { ChoiceSheet, ConfirmSheet } from './ConfirmSheet'
import { DescriptionEditor } from './DescriptionEditor'
import { FollowUpSheet } from './FollowUpSheet'
import { NewCategoryChip } from './NewCategoryChip'
import { RecurrenceSheet } from './RecurrenceSheet'
import { DateRow, Row, RowCard, TimeRow } from './Rows'
import { Segmented } from './Segmented'
import { Sheet } from './Sheet'
import { Switch } from './Switch'
import { LocateIcon, LockIcon } from './icons'
import './DetailPanel.css'

interface DetailPanelProps {
  taskId: string
  /** Where Close returns to. */
  returnTo: string
  /** A past week's row or a preview in a later week: shown, not changed. */
  readOnly?: boolean
}

const KIND_HELP: Record<TaskKind, string> = {
  once: 'Done once.',
  repeat: 'On a set pattern, like every Tuesday.',
  routine: 'Once a week, on whatever day you put it.',
}

/**
 * The edit screen: a bottom sheet on phones, a side panel on wide screens.
 * Every change saves as it is made, so there is no Save and no Cancel.
 */
export function DetailPanel({ taskId, returnTo, readOnly = false }: DetailPanelProps) {
  const navigate = useNavigate()
  const task = useTasks((s) => s.byId[taskId])
  const ready = useTasks((s) => s.ready)
  const close = () => navigate(returnTo, { replace: true })

  if (!task) {
    return (
      <Sheet open onClose={close} heading={ready ? 'Task' : 'Loading'}>
        <p className="panel__gone">{ready ? 'This task is gone.' : 'Loading…'}</p>
      </Sheet>
    )
  }

  // closes the panel and brings the task into view where it sits, switching off any filter that hides it
  const goTo = () => {
    close()
    void goToTask(task.id, navigate)
  }
  const locate =
    !readOnly && isOpen(task) ? (
      <button type="button" className="icon-btn" aria-label="Go to this task" title="Go to this task" onClick={goTo}>
        <LocateIcon size={20} />
      </button>
    ) : undefined

  return (
    <Sheet open onClose={close} heading={readOnly ? 'Task' : 'Edit task'} headerStart={locate}>
      {readOnly && (
        <p className="panel__ro-note">
          {isOpen(task) ? "A future occurrence. It opens here, but it can't be changed until this week's one is done." : 'From a past week. Read-only.'}
        </p>
      )}
      <fieldset className="panel__fields" disabled={readOnly}>
        <PanelBody task={task} onClosed={close} readOnly={readOnly} />
      </fieldset>
    </Sheet>
  )
}

function PanelBody({ task, onClosed, readOnly }: { task: Task; onClosed: () => void; readOnly: boolean }) {
  const t = task
  const today = useToday()
  const people = usePeople()
  const uid = people.meUid
  const categories = useCategories((s) => s.all)
  const settings = useHousehold((s) => s.userDoc?.settings)
  const actions = useTaskActions()

  const [askFollowUp, setAskFollowUp] = useState(false)
  const [askShare, setAskShare] = useState<{ assignee: string | null } | null>(null)
  const [dontAskAgain, setDontAskAgain] = useState(false)
  const [askRepeat, setAskRepeat] = useState(false)
  const [pendingDue, setPendingDue] = useState<{ dueDate: DateStr; dueTime?: TimeStr | null } | null>(null)

  const patch = (change: TaskPatch) => actions.patch(t, change)

  const title = useDraft(t.title, (v) => {
    const next = v.trim().slice(0, 200)
    if (next && next !== t.title) patch({ title: next })
  })
  const description = useDraft(t.description ?? '', (v) => {
    const next = v.trim() === '' ? null : v
    if (next !== (t.description ?? null)) patch({ description: next })
  })

  // ---- people: who's doing it, and private ----
  const isCreator = t.createdBy === uid
  const isPrivate = t.scope === 'private'
  const assignedTo = isPrivate ? uid : (t.assigneeUid ?? 'either')
  const shareWith = (assignee: string | null) => {
    if (settings?.askBeforeSharing === false) {
      actions.setScope(t, 'shared', assignee)
      return
    }
    setDontAskAgain(false)
    setAskShare({ assignee })
  }
  const onWho = (v: string) => {
    const assignee = v === 'either' ? null : v
    if (isPrivate) {
      // a private task is the creator's alone; giving it to anyone else shares it first
      if (assignee !== uid) shareWith(assignee)
      return
    }
    patch({ assigneeUid: assignee })
  }
  const onPrivate = () => {
    if (isPrivate) {
      // turning Private off only lets the other person see it; it stays yours
      shareWith(uid)
      return
    }
    // making a task private hides it from no one but the other person, so it needs no check
    if (isCreator) actions.setScope(t, 'private')
  }
  const confirmShare = () => {
    const assignee = askShare?.assignee ?? null
    setAskShare(null)
    if (dontAskAgain) void updateSettings(uid, { askBeforeSharing: false })
    actions.setScope(t, 'shared', assignee)
  }

  // ---- when ----
  const onPlanned = (d: DateStr | null) => {
    if (d) actions.planOn(t, d, undefined, false)
    else patch({ plannedDate: null })
  }
  const isSchedule = t.kind === 'repeat' && t.recurrence?.basis === 'schedule'
  const changeDue = (change: { dueDate: DateStr; dueTime?: TimeStr | null }) => {
    if (isSchedule) setPendingDue(change)
    else patch(change)
  }
  const onDue = (d: DateStr | null) => {
    if (!d) {
      // a repeat must keep a due date
      if (t.kind === 'repeat') return
      patch({ dueDate: null, dueTime: null, reminder: null })
      return
    }
    changeDue({ dueDate: d })
  }
  const timed = !!t.dueTime
  const onAllDay = () => {
    if (timed) patch({ dueTime: null, reminder: null })
    else if (isSchedule) changeDue({ dueDate: t.dueDate!, dueTime: '09:00' })
    else patch({ dueTime: '09:00', reminder: { offsetMin: 0 } })
  }
  const onDueTime = (v: TimeStr) => changeDue({ dueDate: t.dueDate!, dueTime: v })
  const onRepeat = (r: Recurrence | null) => {
    if (r) patch({ recurrence: { ...r, scheduledDate: t.dueDate ?? today, scheduledTime: t.dueTime } })
  }
  const onBasis = (basis: 'schedule' | 'completion') => {
    if (t.recurrence) patch({ recurrence: { ...t.recurrence, basis } })
  }
  const lastDone = t.completions?.length ? t.completions[t.completions.length - 1] : null

  // a routine's week: Monday to Sunday of the occurrence it is on now
  const routine = t.kind === 'routine' ? routineWeek(t, today) : null
  const routineDays = routine ? Array.from({ length: 7 }, (_, i) => addDays(routine.weekOf, i)) : []

  // ---- waiting ----
  const onWaiting = () => {
    if (t.waiting) actions.setWaiting(t, false)
    else setAskFollowUp(true)
  }

  const createdOn = t.createdAt ? fmtDateLong(dateOf(t.createdAt.toDate()), today) : ''
  const editedBy = t.updatedBy !== t.createdBy ? people.names[t.updatedBy] : null
  const editedOn = t.updatedAt ? fmtDateLong(dateOf(t.updatedAt.toDate()), today) : ''
  const pickable = activeCategories(categories).filter((c) => isPrivate || c.scope === 'shared')
  const categoryMissing = !!t.categoryId && !pickable.some((c) => c.id === t.categoryId) && categories.some((c) => c.id === t.categoryId)
  const privateCategory = isPrivate ? categories.find((c) => c.id === t.categoryId && c.scope === 'private') : undefined
  const otherName = people.other?.name ?? 'The other person'
  const staysMine = askShare?.assignee === uid
  const shareName = staysMine || !askShare?.assignee ? otherName : (people.names[askShare.assignee] ?? otherName)

  return (
    <>
      <div className="panel__card panel__title-card">
        <label className="sr-only" htmlFor="task-title">
          Title
        </label>
        <input
          id="task-title"
          type="text"
          className="panel__title"
          value={title.value}
          maxLength={200}
          onChange={(e) => title.onChange(e.target.value)}
          onFocus={title.onFocus}
          onBlur={title.onBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
        />
        <DescriptionEditor label="Description" placeholder="Add a description" value={description.value} onChange={description.onChange} onBlur={description.onBlur} />
        <Checklist steps={t.checklist ?? []} onChange={(checklist) => patch({ checklist })} />
      </div>

      <section className="panel__section">
        <h3 className="eyebrow panel__eyebrow">When</h3>
        <Segmented<TaskKind>
          label="Kind"
          fill
          value={t.kind}
          options={[
            { value: 'once', label: 'One-off' },
            { value: 'repeat', label: 'Repeats' },
            { value: 'routine', label: 'Routine' },
          ]}
          onChange={(k) => actions.setKind(t, k)}
        />
        <p className="panel__helper">{KIND_HELP[t.kind]}</p>
        <RowCard>
          {t.kind === 'routine' ? (
            <Row label="This week" tall>
              <label className="sr-only" htmlFor="task-week-day">
                This week
              </label>
              <select
                id="task-week-day"
                className="panel__select"
                value={routine?.plannedDate ?? ''}
                onChange={(e) => (e.target.value ? actions.planOn(t, e.target.value, undefined, false) : actions.toTray(t, 'routines'))}
              >
                <option value="">Not placed</option>
                {routineDays.map((d) => (
                  <option key={d} value={d}>
                    {dayHeading(d, today)}
                  </option>
                ))}
              </select>
            </Row>
          ) : (
            <>
              <DateRow id="task-planned" label={t.kind === 'repeat' ? 'Do it on (this one)' : 'Do it on'} value={t.plannedDate} placeholder="Pick a day" onChange={onPlanned} />
              <DateRow id="task-due" label="Due date" value={t.dueDate} placeholder="Add a date" onChange={onDue} clearable={t.kind !== 'repeat'} />
              {t.dueDate && (
                <Row label="All day" onClick={onAllDay} ariaPressed={!timed}>
                  <Switch on={!timed} />
                </Row>
              )}
              {timed && <TimeRow id="task-time" label="Due time" value={t.dueTime!} onChange={onDueTime} />}
              {t.kind === 'repeat' && (
                <>
                  <Row label="Repeats" onClick={() => setAskRepeat(true)}>
                    <span>{repeatLabel(t.recurrence)}</span>
                  </Row>
                  <Row label="Next date from" tall>
                    <Segmented<'schedule' | 'completion'>
                      label="Next date from"
                      value={t.recurrence?.basis ?? 'schedule'}
                      options={[
                        { value: 'schedule', label: 'Schedule' },
                        { value: 'completion', label: 'Last done' },
                      ]}
                      onChange={onBasis}
                    />
                  </Row>
                </>
              )}
            </>
          )}
          <Row label="Waiting on someone" onClick={onWaiting} ariaPressed={t.waiting}>
            <Switch on={t.waiting} />
          </Row>
          {t.waiting && (
            <Row label="Follow up" onClick={() => setAskFollowUp(true)}>
              <span className={t.followUpDate ? '' : 'row__value--muted'}>{t.followUpDate ? `${dayHeading(t.followUpDate, today)}${t.followUpTime ? `, ${t.followUpTime}` : ''}` : 'No date'}</span>
            </Row>
          )}
        </RowCard>
        {t.kind === 'routine' && <p className="panel__note">No due date and no repeat rule for a routine.</p>}
        {/* ticking and unticking belong to the ring on the card; the panel only says when */}
        {t.kind === 'once' && t.done && t.doneOn && <p className="panel__note">Done {fmtDate(t.doneOn)}</p>}
        {lastDone && <p className="panel__note">Last done {fmtDate(lastDone.doneOn)}</p>}
      </section>

      {timed && t.kind !== 'routine' && (
        <section className="panel__section">
          <h3 className="eyebrow panel__eyebrow">Reminder</h3>
          <div className="panel__chips" role="group" aria-label="Reminder">
            <button type="button" className={`chip chip--pick ${!t.reminder ? 'chip--on' : ''}`} aria-pressed={!t.reminder} onClick={() => patch({ reminder: null })}>
              None
            </button>
            {REMINDER_OFFSETS.map((o) => {
              const on = t.reminder?.offsetMin === o.offsetMin
              return (
                <button type="button" key={o.offsetMin} className={`chip chip--pick ${on ? 'chip--on' : ''}`} aria-pressed={on} onClick={() => patch({ reminder: { offsetMin: o.offsetMin } })}>
                  {o.label}
                </button>
              )
            })}
          </div>
        </section>
      )}

      <section className="panel__section">
        <h3 className="eyebrow panel__eyebrow">Category</h3>
        <div className="panel__chips" role="group" aria-label="Category">
          {pickable.map((c) => (
            <button type="button" key={c.id} className={`chip chip--pick ${t.categoryId === c.id ? 'chip--on' : ''}`} aria-pressed={t.categoryId === c.id} onClick={() => patch({ categoryId: c.id })}>
              <span className="chip__dot" style={{ background: `var(--${c.color})` }} />
              {c.name}
              {c.scope === 'private' && <LockIcon size={12} title="Private category" />}
            </button>
          ))}
          <button type="button" className={`chip chip--pick ${!t.categoryId ? 'chip--on' : ''}`} aria-pressed={!t.categoryId} onClick={() => patch({ categoryId: null })}>
            <span className="chip__dot" style={{ background: 'var(--faint)' }} />
            None
          </button>
          <NewCategoryChip scope={isPrivate ? 'private' : 'shared'} onPick={(categoryId) => patch({ categoryId })} />
        </div>
        {categoryMissing && <p className="panel__note">Its category is archived; it stays until you pick another.</p>}
      </section>

      <section className="panel__section">
        <h3 className="eyebrow panel__eyebrow">People</h3>
        <RowCard>
          <Row label="Who's doing it" tall>
            <Segmented<string>
              label="Who's doing it"
              value={assignedTo}
              options={[
                { value: 'either', label: 'Either of us' },
                { value: uid, label: 'Me' },
                ...(people.other ? [{ value: people.other.uid, label: people.other.name }] : []),
              ]}
              onChange={onWho}
            />
          </Row>
          <Row
            label="Private"
            onClick={onPrivate}
            ariaPressed={isPrivate}
            disabled={!isPrivate && !isCreator}
            hint={!isPrivate && !isCreator ? `Only ${people.names[t.createdBy] ?? 'its creator'} can make this private.` : isPrivate ? 'Only you can see this task.' : undefined}
          >
            <Switch on={isPrivate} disabled={!isPrivate && !isCreator} />
          </Row>
        </RowCard>
      </section>

      {!readOnly && (
        <footer className="panel__footer">
          <span className="panel__made">
            Created by {people.names[t.createdBy] ?? 'someone'}, {createdOn}.{editedBy ? ` Edited by ${editedBy}, ${editedOn}.` : ''} Changes save as you make them.
          </span>
          <Button
            variant="danger"
            onClick={() => {
              actions.remove(t)
              onClosed()
            }}
          >
            Delete
          </Button>
        </footer>
      )}

      <FollowUpSheet
        open={askFollowUp}
        task={t}
        today={today}
        onPick={(d, time) => {
          setAskFollowUp(false)
          actions.setWaiting(t, true, d, time)
        }}
        onCancel={() => setAskFollowUp(false)}
      />
      <ConfirmSheet
        open={!!askShare}
        heading={staysMine ? `Let ${otherName} see this task?` : `Share this task with ${shareName}?`}
        message={`${otherName} will see it.${staysMine ? ' It stays yours.' : ''}${privateCategory ? ` Its private category, ${privateCategory.name}, comes off.` : ''}`}
        confirmLabel={staysMine ? 'Make visible' : 'Share'}
        onConfirm={confirmShare}
        onCancel={() => setAskShare(null)}
      >
        <label className="confirm__check">
          <input type="checkbox" checked={dontAskAgain} onChange={(e) => setDontAskAgain(e.target.checked)} />
          Don't ask again
        </label>
      </ConfirmSheet>
      {t.kind === 'repeat' && (
        <RecurrenceSheet key={repeatLabel(t.recurrence)} open={askRepeat} dueDate={t.dueDate ?? today} value={t.recurrence} onChange={onRepeat} onClose={() => setAskRepeat(false)} />
      )}
      <ChoiceSheet
        open={!!pendingDue}
        heading="Change which?"
        message="This task repeats on a schedule."
        choices={[
          { key: 'one', label: 'Just this one', hint: 'The next one follows the pattern' },
          { key: 'future', label: 'This and future', hint: 'Moves the pattern too' },
        ]}
        onChoose={(k) => {
          const change = pendingDue!
          setPendingDue(null)
          patch(moveOccurrence(t, change, k === 'future' ? 'future' : 'one'))
        }}
        onCancel={() => setPendingDue(null)}
      />
    </>
  )
}
